import {
  Component,
  Directive,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  NgZone,
  ElementRef,
  OnChanges,
  SimpleChanges,
  ContentChild,
  AfterContentInit
} from '@angular/core';

@Directive({ selector: '[dropdown-header]' })
export class DropdownHeaderDirective {}

@Directive({ selector: '[dropdown-footer]' })
export class DropdownFooterDirective {}

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost' | 'text' | 'gray';
export type ButtonSize = 'small' | 'medium' | 'large';
export type IconPosition = 'left' | 'right';

export interface DropdownItem {
  label: string;
  value: any;
  checked: boolean;
}

@Component({
  selector: 'app-button',
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss']
})
export class ButtonComponent
  implements OnInit, OnDestroy, OnChanges, AfterContentInit {

  @Input() variant: ButtonVariant = 'primary';
  @Input() size: ButtonSize = 'medium';
  @Input() disabled: boolean = false;
  @Input() loading: boolean = false;
  @Input() type: 'button' | 'submit' | 'reset' = 'button';
  @Input() icon?: string;
  @Input() iconPosition: IconPosition = 'left';
  @Input() ariaLabel?: string;
  @Input() isDropdown: boolean = false;
  @Input() dropdownHeader: string = '';
  @Input() dropdownFooter: string = '';
  @Input() dropdownItems: DropdownItem[] = [];
  @Input() dropdownPortal: boolean = false;
  @Input() noBorder: boolean = false;
  @Input() selectFirstByDefault: boolean = false;

  @Input() singleSelect: boolean = false;
  @Input() radioSelect: boolean = false;
  @Input() hideChevron: boolean = false;

  @Output() buttonClick = new EventEmitter<void>();
  @Output() dropdownChange = new EventEmitter<DropdownItem[]>();
  @Output() itemClick = new EventEmitter<DropdownItem>();

  @ContentChild(DropdownHeaderDirective) headerContent: DropdownHeaderDirective;
  @ContentChild(DropdownFooterDirective) footerContent: DropdownFooterDirective;

  isDropdownOpen = false;
  dropdownFlipsUp: boolean = false;
  internalDropdownItems: DropdownItem[] = [];

  dropdownPosition: any = null;

  private boundClickHandler: any;
  private boundKeydownHandler: any;

  hasProjectedHeader = false;
  hasProjectedFooter = false;

  constructor(private ngZone: NgZone, private elementRef: ElementRef) {
    this.boundClickHandler = (event: MouseEvent) => this.onDocumentClick(event);
    this.boundKeydownHandler = (event: KeyboardEvent) => this.onDocumentKeydown(event);
  }

  ngOnInit(): void {
    this.syncItems();
    this.updateListeners();
  }

  ngAfterContentInit(): void {
    this.hasProjectedHeader = !!this.headerContent;
    this.hasProjectedFooter = !!this.footerContent;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dropdownItems']) {
      this.syncItems();
    }

    if (changes['isDropdown']) {
      this.updateListeners();
    }
  }

  ngOnDestroy(): void {
    this.removeListeners();
  }

  private syncItems(): void {
    this.internalDropdownItems = (this.dropdownItems || []).map((i, index) => ({
      ...i,
      checked: this.selectFirstByDefault && index === 0 ? true : i.checked
    }));
  }

  private updateListeners(): void {
    this.removeListeners();

    if (this.isDropdown) {
      this.ngZone.runOutsideAngular(() => {
        document.addEventListener('click', this.boundClickHandler);
        document.addEventListener('keydown', this.boundKeydownHandler);
      });
    }
  }

  private removeListeners(): void {
    document.removeEventListener('click', this.boundClickHandler);
    document.removeEventListener('keydown', this.boundKeydownHandler);
  }

  private onDocumentClick(event: MouseEvent): void {
    if (!this.isDropdownOpen) return;

    const target = event.target as HTMLElement;
    const isInside = this.elementRef.nativeElement.contains(target);

    if (!isInside) {
      this.ngZone.run(() => this.closeDropdown());
    }
  }

  private onDocumentKeydown(event: KeyboardEvent): void {
    if (!this.isDropdownOpen) return;

    if (event.key === 'Escape') {
      this.closeDropdown();
    }
  }

  onButtonClick(event: MouseEvent): void {
    if (this.disabled || this.loading) return;

    if (this.isDropdown) {
      event.stopPropagation();
      this.toggleDropdown();
    } else {
      this.buttonClick.emit();
    }
  }

  onButtonKeydown(event: KeyboardEvent): void {
    if (!this.isDropdown) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.toggleDropdown();
    }
  }

  toggleDropdown(): void {
    if (!this.isDropdownOpen) {
      this.computeFlip();
    }
    this.isDropdownOpen = !this.isDropdownOpen;

    if (this.isDropdownOpen && this.dropdownPortal) {
      this.calculateDropdownPosition();
    }
  }

  private computeFlip(): void {
    const button = this.elementRef.nativeElement.querySelector('button');
    if (!button) { return; }
    const rect = button.getBoundingClientRect();
    const ESTIMATED_HEIGHT = 280;
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;
    this.dropdownFlipsUp = spaceBelow < ESTIMATED_HEIGHT && spaceAbove > spaceBelow;
  }

  closeDropdown(): void {
    this.isDropdownOpen = false;
  }

  private calculateDropdownPosition(): void {
    requestAnimationFrame(() => {
      const button = this.elementRef.nativeElement.querySelector('button');
      if (!button) { return; }

      const rect = button.getBoundingClientRect();

      if (this.dropdownFlipsUp) {
        this.dropdownPosition = {
          position: 'fixed',
          bottom: (window.innerHeight - rect.top + 8) + 'px',
          left: rect.left + 'px',
          zIndex: 1000
        };
      } else {
        this.dropdownPosition = {
          position: 'fixed',
          top: (rect.bottom + 8) + 'px',
          left: rect.left + 'px',
          zIndex: 1000
        };
      }
    });
  }

  onDropdownContentClick(event: Event): void {
    event.stopPropagation();
  }

  onItemChange(item: DropdownItem, checked: boolean): void {
    this.internalDropdownItems = this.internalDropdownItems.map(i =>
      i.value === item.value ? { ...i, checked } : i
    );

    this.dropdownChange.emit(
      this.internalDropdownItems.filter(i => i.checked)
    );
  }

  onSingleItemClick(item: DropdownItem): void {
    this.internalDropdownItems = this.internalDropdownItems.map(i => ({
      ...i,
      checked: i.value === item.value
    }));
    this.itemClick.emit(item);
    this.closeDropdown();
  }

  onRadioItemClick(item: DropdownItem): void {
    this.internalDropdownItems = this.internalDropdownItems.map(i => ({
      ...i,
      checked: i.value === item.value
    }));
    this.dropdownChange.emit(this.internalDropdownItems.filter(i => i.checked));
  }

  trackByValue(index: number, item: DropdownItem): any {
    return item.value;
  }

  get buttonClasses(): string {
    const classes = [
      `btn-${this.variant}`,
      `btn-${this.size}`
    ];

    if (this.disabled) classes.push('btn-disabled');
    if (this.loading) classes.push('btn-loading');
    if (this.icon) classes.push('btn-with-icon');
    if (this.isDropdown) classes.push('btn-dropdown');
    if (this.noBorder) classes.push('btn-no-border');

    return classes.join(' ');
  }
}